import * as React from "react";
import type { SAMethodBlock, SASectionCopy } from "@/data/sellerAnalysisTypes";
import { Reveal, Overline } from "@/components/presentation/primitives";
import { DataTag } from "./DataTag";

interface Props {
  methodology: SAMethodBlock[];
  intro?: SASectionCopy;
}

const Block: React.FC<{ block: SAMethodBlock; index: number }> = ({ block, index }) => (
  <div className="border border-charcoal/12 bg-ivory-surface p-7 md:p-9">
    <div className="flex items-baseline gap-4">
      <span className="font-serif text-2xl text-brass md:text-3xl">
        {String(index + 1).padStart(2, "0")}
      </span>
      <h3 className="font-serif text-2xl leading-snug text-charcoal md:text-3xl">
        {block.title}
      </h3>
    </div>

    {block.intro && (
      <p className="mt-4 max-w-2xl text-base leading-relaxed text-charcoal-soft md:text-lg">
        {block.intro}
      </p>
    )}

    <div className="mt-7 divide-y divide-charcoal/10 border-y border-charcoal/10">
      {block.rows.map((row, i) => (
        <div
          key={i}
          className="flex flex-col gap-2 py-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6"
        >
          <div className="flex items-start gap-3">
            {row.operator && (
              <span aria-hidden className="font-serif text-lg text-brass">
                {row.operator}
              </span>
            )}
            <div>
              <p className="text-base text-charcoal md:text-lg">{row.label}</p>
              {row.note && (
                <p className="mt-0.5 text-sm text-charcoal-muted">{row.note}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 pl-6 sm:pl-0">
            {row.value && (
              <span className="font-serif text-lg text-charcoal md:text-xl">
                {row.value}
              </span>
            )}
            {row.kind && <DataTag kind={row.kind} />}
          </div>
        </div>
      ))}
    </div>

    {block.result && (
      <div className="mt-6 flex flex-col gap-2 border-t-2 border-brass pt-6 sm:flex-row sm:items-baseline sm:justify-between">
        <div>
          <p className="text-[0.7rem] uppercase tracking-[0.18em] text-charcoal-muted">
            {block.result.label}
          </p>
          {block.result.note && (
            <p className="mt-1 text-sm text-charcoal-muted">{block.result.note}</p>
          )}
        </div>
        <p className="font-serif text-2xl text-charcoal md:text-3xl">
          {block.result.value}
        </p>
      </div>
    )}
  </div>
);

export const SA_Methodology: React.FC<Props> = ({ methodology, intro }) => {
  if (!methodology?.length) return null;

  return (
    <section
      className="w-full bg-ivory py-20 md:py-28"
      data-testid="sa-methodology"
      aria-label="How I value your home"
    >
      <div className="mx-auto w-full max-w-4xl px-6 md:px-10">
        <Reveal>
          <Overline>{intro?.overline || "How I value your home"}</Overline>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-charcoal md:text-4xl lg:text-5xl">
            {intro?.headline || "How I got to the range."}
          </h2>
        </Reveal>

        <div className="mt-12 space-y-6">
          {methodology.map((block, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <Block block={block} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
